import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiClock, FiTrendingUp, FiChevronRight, FiCheck } = FiIcons;

const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to MindGarden! 🌱',
    description: 'Your digital sanctuary for mindful productivity and personal growth.',
    content: (
      <div className="text-center space-y-4">
        <div className="w-32 h-32 rounded-3xl flex items-center justify-center mx-auto mb-6 overflow-hidden bg-white border border-sage/20">
          <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758314084196-MG-%2B-Wordmark-Logo.jpg" alt="MindGarden Logo" className="w-full h-full object-contain" />
        </div>
        <p className="text-charcoal/80 font-dm-sans leading-relaxed">
          MindGarden combines the engaging mechanics of idle games with proven productivity techniques. Plant seeds of intention, nurture your focus, and watch your digital garden flourish as you build lasting habits.
        </p>
      </div>
    )
  },
  {
    id: 'goals',
    title: 'What brings you here?',
    description: 'Help us personalize your experience.',
    content: (
      <div className="space-y-3">
        {[
          { icon: FiTarget, text: 'Build better focus habits', value: 'focus' },
          { icon: FiTrendingUp, text: 'Increase productivity', value: 'productivity' },
          { icon: FiClock, text: 'Manage time better', value: 'time' },
          { icon: SafeIcon, text: 'Practice mindfulness', value: 'mindfulness' }
        ].map((goal, index) => (
          <motion.button
            key={goal.value}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="w-full p-4 text-left border-2 border-sage/20 rounded-xl hover:border-sage/40 hover:bg-sage/5 transition-all duration-200"
          >
            <div className="flex items-center gap-3">
              <SafeIcon icon={goal.icon} className="w-5 h-5 text-sage" />
              <span className="font-dm-sans text-charcoal">{goal.text}</span>
            </div>
          </motion.button>
        ))}
      </div>
    )
  },
  {
    id: 'features',
    title: 'Core Features',
    description: 'Discover what makes MindGarden special.',
    content: (
      <div className="space-y-4">
        <div className="flex items-start gap-4 p-4 bg-sage/5 rounded-xl">
          <div className="w-10 h-10 bg-sage/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiClock} className="w-5 h-5 text-sage" />
          </div>
          <div>
            <h3 className="font-dm-sans font-semibold text-charcoal mb-1">Focus Sessions</h3>
            <p className="text-sm text-charcoal/70">Pomodoro-style timers that reward you with seeds and build streaks</p>
          </div>
        </div>
        <div className="flex items-start gap-4 p-4 bg-skyblue/5 rounded-xl">
          <div className="w-10 h-10 bg-skyblue/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <div className="w-5 h-5 rounded overflow-hidden">
              <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758314084196-MG-%2B-Wordmark-Logo.jpg" alt="MindGarden" className="w-full h-full object-contain" />
            </div>
          </div>
          <div>
            <h3 className="font-dm-sans font-semibold text-charcoal mb-1">Digital Garden</h3>
            <p className="text-sm text-charcoal/70">Plant thoughts, harvest insights, and watch your garden grow automatically</p>
          </div>
        </div>
        <div className="flex items-start gap-4 p-4 bg-goldenrod/5 rounded-xl">
          <div className="w-10 h-10 bg-goldenrod/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-goldenrod" />
          </div>
          <div>
            <h3 className="font-dm-sans font-semibold text-charcoal mb-1">Progress Tracking</h3>
            <p className="text-sm text-charcoal/70">Detailed analytics and insights into your productivity patterns</p>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'preferences',
    title: 'Customize Your Experience',
    description: 'Set your preferences to get started.',
    content: (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-dm-sans font-medium text-charcoal mb-3">
            Preferred Focus Session Length
          </label>
          <div className="grid grid-cols-2 gap-3">
            {[15, 25, 45, 60].map((minutes) => (
              <button key={minutes} className="p-3 border-2 border-sage/20 rounded-xl hover:border-sage/40 hover:bg-sage/5 transition-all">
                <div className="text-lg font-bold text-charcoal">{minutes}</div>
                <div className="text-sm text-charcoal/60">minutes</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-dm-sans font-medium text-charcoal mb-3">
            Daily Goal
          </label>
          <div className="grid grid-cols-1 gap-2">
            {[
              '1 focus session per day',
              '30 minutes of focus time',
              '2-3 focus sessions per day',
              'I\'ll set this later'
            ].map((goal, index) => (
              <button key={index} className="p-3 text-left border-2 border-sage/20 rounded-xl hover:border-sage/40 hover:bg-sage/5 transition-all">
                <span className="font-dm-sans text-charcoal">{goal}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'complete',
    title: 'You\'re All Set! 🎉',
    description: 'Your MindGarden is ready to flourish.',
    content: (
      <div className="text-center space-y-6">
        <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto overflow-hidden bg-white border border-sage/20">
          <img src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758314084196-MG-%2B-Wordmark-Logo.jpg" alt="MindGarden Logo" className="w-full h-full object-contain" />
        </div>
        <div className="space-y-4">
          <p className="text-charcoal/80 font-dm-sans leading-relaxed">
            Welcome to your mindful productivity journey! You've received <strong>100 starter seeds</strong> to begin growing your garden.
          </p>
          <div className="grid grid-cols-1 gap-3">
            <div className="p-3 bg-sage/10 rounded-xl border border-sage/20">
              <div className="text-sm font-dm-sans text-charcoal">
                💡 <strong>Quick Tip:</strong> Start with a 25-minute focus session to earn your first rewards
              </div>
            </div>
            <div className="p-3 bg-skyblue/10 rounded-xl border border-skyblue/20">
              <div className="text-sm font-dm-sans text-charcoal">
                🌱 <strong>Pro Tip:</strong> Visit the Thought Garden to plant your first mindful reflection
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

export default function OnboardingFlow({ isOpen, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete onboarding
      const onboardingData = {
        completed: true,
        completedAt: new Date().toISOString(),
        preferences: selectedOptions
      };
      localStorage.setItem('mindgarden-onboarding', JSON.stringify(onboardingData));
      onComplete(onboardingData);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentStepData = ONBOARDING_STEPS[currentStep];
  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-gradient-to-br from-beige via-white to-sage/10 z-50 flex items-center justify-center p-4"
    >
      <div className="w-full max-w-2xl">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-dm-sans text-charcoal/60">
              Step {currentStep + 1} of {ONBOARDING_STEPS.length}
            </span>
            <span className="text-sm font-dm-sans text-charcoal/60">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-sage/20 rounded-full h-2">
            <motion.div
              className="bg-sage h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="p-8">
              <div className="text-center mb-8">
                <h1 className="text-2xl lg:text-3xl font-cormorant font-bold text-charcoal mb-2">
                  {currentStepData.title}
                </h1>
                <p className="text-charcoal/60 font-dm-sans">
                  {currentStepData.description}
                </p>
              </div>
              <div className="mb-8">
                {currentStepData.content}
              </div>
              {/* Navigation */}
              <div className="flex justify-between items-center">
                <div>
                  {currentStep > 0 && (
                    <Button variant="secondary" onClick={handlePrevious} >
                      Previous
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  {currentStep < ONBOARDING_STEPS.length - 1 && (
                    <Button variant="secondary" onClick={() => onComplete({ skipped: true })} >
                      Skip
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={handleNext}
                    icon={currentStep === ONBOARDING_STEPS.length - 1 ? FiCheck : FiChevronRight}
                    iconPosition="right"
                  >
                    {currentStep === ONBOARDING_STEPS.length - 1 ? 'Start Gardening' : 'Continue'}
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}